import React, { useState, useEffect } from 'react';
import { useErrand, Errand, ExtraCharge, CreateExtraChargeData } from '../contexts/ErrandContext';
import { toast } from 'react-hot-toast';

const ExtraChargesPanel: React.FC = () => {
  const { driverErrands, fetchDriverErrands, createExtraCharge, getErrandExtraCharges, errandExtraCharges, loading } = useErrand();
  const [selectedErrandId, setSelectedErrandId] = useState<string>('');
  const [chargeType, setChargeType] = useState<ExtraCharge['charge_type']>('other');
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState<string>('');
  const [evidenceUrl, setEvidenceUrl] = useState('');

  useEffect(() => {
    fetchDriverErrands();
  }, [fetchDriverErrands]);

  useEffect(() => {
    if (selectedErrandId) {
      getErrandExtraCharges(selectedErrandId);
    }
  }, [selectedErrandId, getErrandExtraCharges]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedErrandId) {
      toast.error('Please select an errand.');
      return;
    }
    const chargeAmount = parseFloat(amount);
    if (isNaN(chargeAmount) || chargeAmount <= 0) {
      toast.error('Please enter a valid amount.');
      return;
    }
    if (!description.trim()) {
      toast.error('Please provide a description for the charge.');
      return;
    }

    const data: CreateExtraChargeData = {
      errandId: selectedErrandId,
      chargeType,
      description,
      amount: chargeAmount,
      evidenceUrl: evidenceUrl.trim() || undefined,
    };

    const result = await createExtraCharge(data);
    if (result) {
      toast.success('Extra charge submitted successfully!');
      // Reset form
      setDescription('');
      setAmount('');
      setEvidenceUrl('');
    } else {
      toast.error('Failed to submit extra charge.');
    }
  };

  const activeErrands = driverErrands.filter(
    (errand) => errand.status === 'accepted' || errand.status === 'in_progress' || errand.status === 'picked_up'
  );

  const currentCharges = errandExtraCharges[selectedErrandId]?.extra_charges || [];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      case 'paid':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  
  return (
    <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-4 sm:p-6">
      <h2 className="text-base sm:text-lg font-semibold text-neutral-800 border-b border-neutral-200 pb-4 mb-4">
        Submit Extra Charges
      </h2>

      {activeErrands.length === 0 ? (
         <div className="text-center py-8 text-neutral-500">
            You have no active errands to which you can add charges.
         </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="errand" className="block text-sm font-medium text-gray-700">
              Select an Active Errand
            </label>
            <select
              id="errand"
              value={selectedErrandId}
              onChange={(e) => setSelectedErrandId(e.target.value)}
              className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
            >
              <option value="">-- Select Errand --</option>
              {activeErrands.map((errand) => (
                <option key={errand.id} value={errand.id}>
                  {errand.title} ({errand.id.substring(0, 8)})
                </option>
              ))}
            </select>
          </div>

          {selectedErrandId && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="chargeType" className="block text-sm font-medium text-gray-700">
                    Charge Type
                  </label>
                  <select
                    id="chargeType"
                    value={chargeType}
                    onChange={(e) => setChargeType(e.target.value as ExtraCharge['charge_type'])}
                    className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                  >
                    <option value="transport">Transport</option>
                    <option value="toll">Toll</option>
                    <option value="parking">Parking</option>
                    <option value="waiting">Waiting</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="amount" className="block text-sm font-medium text-gray-700">
                    Amount (BWP)
                  </label>
                  <input
                    type="number"
                    id="amount"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    placeholder="e.g., 25.50"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                  Description
                </label>
                <textarea
                  id="description"
                  rows={3}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  placeholder="e.g., Extra waiting time at the pickup location"
                />
              </div>

              <div>
                <label htmlFor="evidenceUrl" className="block text-sm font-medium text-gray-700">
                  Evidence URL (Optional)
                </label>
                <input
                  type="text"
                  id="evidenceUrl"
                  value={evidenceUrl}
                  onChange={(e) => setEvidenceUrl(e.target.value)}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  placeholder="Link to receipt image, etc."
                />
              </div>

              <div className="text-right">
                <button
                  type="submit"
                  disabled={loading}
                  className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                >
                  {loading ? 'Submitting...' : 'Submit Charge'}
                </button>
              </div>
            </>
          )}
        </form>
      )}

      {selectedErrandId && (
        <div className="mt-8">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Submitted Charges for This Errand</h3>
            {loading && currentCharges.length === 0 ? (
                <p>Loading charges...</p>
            ) : currentCharges.length === 0 ? (
                <p className="text-sm text-gray-500">No extra charges have been submitted for this errand yet.</p>
            ) : (
                <ul className="space-y-3">
                    {currentCharges.map(charge => (
                        <li key={charge.id} className="bg-gray-50 p-4 rounded-lg flex items-center justify-between">
                            <div>
                                <p className="font-semibold">{charge.description}</p>
                                <p className="text-sm text-gray-600">BWP {charge.amount.toFixed(2)}</p>
                            </div>
                            <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(charge.status)}`}>
                                {charge.status}
                            </span>
                        </li>
                    ))}
                </ul>
            )}
        </div>
      )}
    </div>
  );
};

export default ExtraChargesPanel;
